// ThreadNotice — a notice in the thread (pen "Chat – every entry" §8): a turn
// that ended without a reply. A cause you chose (Stopped) is one quiet trail
// line; a cause that needs you is a tinted box in plain words. The action is
// offered only when wired (the transcript wires it on its live tail only), so
// a notice in scrollback reads as history. Never steals focus.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import { TrailLine } from "../TrailLine/TrailLine.js";
import type { InterruptionAction, SystemEntryKind } from "../types.js";
import { type NoticeContext, threadNoticeCopy } from "./threadNoticeCopy.js";
import "./ThreadNotice.css";

export const ThreadNotice = ({
    kind,
    context,
    onAction,
}: {
    readonly kind: SystemEntryKind;
    readonly context: NoticeContext;
    readonly onAction?: (action: InterruptionAction) => void;
}): ReactNode => {
    const { i18n } = useLingui();
    const copy = threadNoticeCopy(kind, context);
    const { action } = copy;
    const wired =
        action === undefined || onAction === undefined
            ? undefined
            : {
                  label: i18n._(action.label),
                  onAction: () => onAction(action.type),
              };
    if (copy.look === "trail")
        return (
            <TrailLine
                glyph="stopped"
                text={i18n._(copy.title)}
                action={wired}
            />
        );
    return (
        <div
            className={`bilda-thread-notice bilda-thread-notice--${copy.look}`}
            role="status"
        >
            <Icon name={copy.icon} className="bilda-thread-notice__glyph" />
            <div className="bilda-thread-notice__text">
                <span className="bilda-thread-notice__title">
                    {i18n._(copy.title)}
                </span>
                {copy.body === undefined ? null : (
                    <span className="bilda-thread-notice__body">
                        {i18n._(copy.body)}
                    </span>
                )}
                {wired === undefined ? null : (
                    <button
                        type="button"
                        className="bilda-thread-notice__action"
                        onClick={wired.onAction}
                    >
                        {wired.label}
                    </button>
                )}
            </div>
        </div>
    );
};
